import React, { useState, useEffect } from "react";
import {
    getEventTypeQuestions,
    addEventTypeQuestion,
    updateEventTypeQuestion,
    deleteEventTypeQuestion
} from "../../../Services/DynamicEventTypes";
import { generateSmartPrefill } from "../../../Services/Gemini";
import { SMART_PREFILL_CONFIG } from "../constants";

const QuestionsManager = ({ eventType, onClose }) => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [form, setForm] = useState({
        question: '',
        choices: ['', ''],
        sort_order: 0
    });
    const [aiPrompt, setAiPrompt] = useState("");
    const [aiApiKey, setAiApiKey] = useState("");
    const [aiLoading, setAiLoading] = useState(false);

    const reload = () => {
        setLoading(true);
        setError(null);
        getEventTypeQuestions(eventType.id)
            .then(res => {
                setQuestions(res.data.data.responseData || []);
            })
            .catch((err) => {
                console.error('Error loading questions:', err);
                setError("Failed to load questions");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        reload();
    }, [eventType.id]);

    const handleAdd = () => {
        setEditingQuestion(null);
        setForm({
            question: '',
            category_id: SMART_PREFILL_CONFIG.defaultCategoryId,
            choices: ['', ''],
            sort_order: questions.length
        });
    };

    const handleEdit = (question) => {
        setEditingQuestion(question);
        const choices = typeof question.choices === 'string' 
            ? JSON.parse(question.choices) 
            : question.choices;
        setForm({
            question: question.question,
            choices: Array.isArray(choices) ? choices : [],
            sort_order: question.sort_order || 0
        });
    };

    const handleSave = async () => {
        if (!form.question || form.choices.filter(c => c.trim()).length < 2) {
            setError("Question and at least 2 choices are required");
            return;
        }

        setError(null);
        const payload = {
            ...form,
            category_id: SMART_PREFILL_CONFIG.defaultCategoryId, // Default category for custom dynamic events
            choices: form.choices.filter(c => c.trim())
        };

        try {
            if (editingQuestion) {
                await updateEventTypeQuestion(eventType.id, editingQuestion.id, payload);
            } else {
                await addEventTypeQuestion(eventType.id, payload);
            }
            reload();
            setEditingQuestion(null);
            setForm({ question: '', choices: ['', ''], sort_order: 0 });
        } catch (err) {
            console.error('Error saving question:', err);
            setError(err.response?.data?.message || "Failed to save question");
        }
    };

    const handleDelete = async (question) => {
        if (window.confirm(`Delete question: "${question.question}"?`)) {
            try {
                await deleteEventTypeQuestion(eventType.id, question.id);
                reload();
            } catch (err) {
                console.error('Error deleting question:', err);
                setError("Failed to delete question");
            }
        }
    };

    const handleAiRebuild = async () => {
        if (!aiPrompt.trim()) {
            setError("Please enter a prompt to generate questions with AI.");
            return;
        }

        setAiLoading(true);
        setError(null);

        try {
            const result = await generateSmartPrefill(aiPrompt, aiApiKey);
            const aiQuestions = Array.isArray(result.questions) ? result.questions : [];

            if (aiQuestions.length === 0) {
                setError("AI did not return any valid questions. Try adjusting your prompt.");
                return;
            }

            // Delete existing questions
            for (const q of questions) {
                try {
                    await deleteEventTypeQuestion(eventType.id, q.id);
                } catch (deleteError) {
                    console.error("Error deleting question during AI rebuild:", deleteError);
                }
            }

            // Create new questions from AI
            for (let index = 0; index < aiQuestions.length; index += 1) {
                const q = aiQuestions[index];
                if (!q?.question || !Array.isArray(q.choices)) {
                    continue;
                }
                const payload = {
                    question: q.question,
                    choices: q.choices,
                    sort_order: Number.isFinite(q.sort_order) ? q.sort_order : index,
                    category_id: SMART_PREFILL_CONFIG.defaultCategoryId
                };
                try {
                    await addEventTypeQuestion(eventType.id, payload);
                } catch (addError) {
                    console.error("Error adding AI-generated question:", addError);
                }
            }

            await reload();
            setEditingQuestion(null);
            setForm({ question: '', choices: ['', ''], sort_order: 0 });
        } catch (err) {
            console.error("Error generating AI questions:", err);
            if (err?.isSmartPrefillError && err.code === "missing_api_key") {
                setError("Gemini is not configured. Add an API key override and try again.");
            } else if (err?.isSmartPrefillError) {
                setError("Failed to generate questions from AI. Please check your key and prompt, then try again.");
            } else {
                setError("Failed to generate questions from AI.");
            }
        } finally {
            setAiLoading(false);
        }
    };

    const handleAddChoice = () => {
        setForm({ ...form, choices: [...form.choices, ''] });
    };

    const handleRemoveChoice = (index) => {
        const newChoices = form.choices.filter((_, i) => i !== index);
        setForm({ ...form, choices: newChoices });
    };

    const handleChoiceChange = (index, value) => {
        const newChoices = [...form.choices];
        newChoices[index] = value;
        setForm({ ...form, choices: newChoices });
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Manage Questions: {eventType.display_name}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}

                        {/* AI Question Builder */}
                        <div className="card mb-3">
                            <div className="card-header">
                                <h6 className="mb-0">AI Question Builder</h6>
                            </div>
                            <div className="card-body">
                                <p className="text-muted small mb-3">
                                    Use Gemini to rebuild all questions for this event type from a prompt. This will
                                    delete existing questions and replace them with AI-generated ones.
                                </p>
                                <div className="mb-3">
                                    <label className="form-label">AI Prompt *</label>
                                    <textarea
                                        className="form-control"
                                        rows={SMART_PREFILL_CONFIG.promptRows}
                                        placeholder="Describe the type of bets/questions you want for this event..."
                                        value={aiPrompt}
                                        onChange={(e) => setAiPrompt(e.target.value)}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Gemini API Key (optional override)</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        placeholder="Leave blank to use stored/default key"
                                        value={aiApiKey}
                                        onChange={(e) => setAiApiKey(e.target.value)}
                                    />
                                    <small className="text-muted">
                                        Key is used only in your browser to call Gemini&apos;s API.
                                    </small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={handleAiRebuild}
                                    disabled={aiLoading}
                                >
                                    {aiLoading ? "Rebuilding Questions..." : "Rebuild Questions with AI"}
                                </button>
                            </div>
                        </div>

                        {/* Question Form */}
                        <div className="card mb-3">
                            <div className="card-header">
                                <h6 className="mb-0">
                                    {editingQuestion ? 'Edit Question' : 'Add New Question'}
                                </h6>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">Question Text *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g., Who will win?"
                                        value={form.question}
                                        onChange={(e) => setForm({ ...form, question: e.target.value })}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Sort Order</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={form.sort_order}
                                        onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) })}
                                        min="0"
                                    />
                                    <small className="text-muted">Controls display order of questions</small>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Choices * (minimum 2)</label>
                                    {form.choices.map((choice, index) => (
                                        <div key={index} className="input-group mb-2">
                                            <span className="input-group-text">{index + 1}</span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Choice text"
                                                value={choice}
                                                onChange={(e) => handleChoiceChange(index, e.target.value)}
                                            />
                                            {form.choices.length > 2 && (
                                                <button
                                                    className="btn btn-outline-danger"
                                                    type="button"
                                                    onClick={() => handleRemoveChoice(index)}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={handleAddChoice}
                                    >
                                        + Add Choice
                                    </button>
                                </div>

                                <div className="d-flex gap-2">
                                    <button className="btn btn-primary" onClick={handleSave}>
                                        {editingQuestion ? 'Update Question' : 'Add Question'}
                                    </button>
                                    {editingQuestion && (
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                setEditingQuestion(null);
                                                setForm({ question: '', choices: ['', ''], sort_order: 0 });
                                            }}
                                        >
                                            Cancel Edit
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Questions List */}
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">Existing Questions ({questions.length})</h6>
                            </div>
                            <div className="card-body">
                                {loading ? (
                                    <div className="text-center py-3">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : questions.length === 0 ? (
                                    <p className="text-muted text-center py-3 mb-0">No questions yet. Add your first question above.</p>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Order</th>
                                                    <th>Question</th>
                                                    <th>Choices</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {questions.map((q) => {
                                                    const choices = typeof q.choices === 'string' 
                                                        ? JSON.parse(q.choices) 
                                                        : q.choices;
                                                    return (
                                                        <tr key={q.id}>
                                                            <td>{q.sort_order}</td>
                                                            <td>{q.question}</td>
                                                            <td>
                                                                {Array.isArray(choices) && choices.map((c, i) => (
                                                                    <span key={i} className="badge bg-light text-dark me-1">
                                                                        {c}
                                                                    </span>
                                                                ))}
                                                            </td>
                                                            <td>
                                                                <div className="btn-group btn-group-sm">
                                                                    <button className="btn btn-outline-primary" onClick={() => handleEdit(q)}>
                                                                        Edit
                                                                    </button>
                                                                    <button className="btn btn-outline-danger" onClick={() => handleDelete(q)}>
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuestionsManager;
